import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ShowNormalQuestionaireFormQuestion, deleteNormalQuestionaireAnswers, editNormalQuestionaireAnswers } from '../../../../services/CommonService'
import { toast } from 'react-toastify'
import Utils from '../../../../utils'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const ManageNormalQuestionnareDetails = () => {
  const navigate = useNavigate()
  const { form_uuid } = useParams()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingSubmission, setEditingSubmission] = useState(null)
  const [editAnswers, setEditAnswers] = useState({})
  const datePickerRefs = useRef({})

  const handleBack = () => {
    navigate('/client-portal/questionnaire/manage')
  }

  useEffect(() => {
    if (form_uuid) {
      fetchFormQuestions()
    }
  }, [form_uuid])

  const fetchFormQuestions = async () => {
    try {
      setLoading(true)
      const response = await ShowNormalQuestionaireFormQuestion({ form_uuid: form_uuid })
      if (response?.data?.data) {
        setFormData(response.data.data)
      } else {
        toast.error('Failed to load form questions')
      }
    } catch (error) {
      console.error('Error fetching form questions:', error)
      toast.error(Utils.getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const renderOptions = (available_options, selected, type) => {
    if (!Array.isArray(available_options) || available_options.length === 0) {
      return <span className="text-muted">No answer provided</span>
    }

    return (
      <div className="smartq-options-inline">
        {available_options.map((opt, idx) => {
          const optionValue = opt.option_text || opt.name
          const isChecked = type === "checkbox"
            ? Array.isArray(selected) && selected.includes(optionValue)
            : selected === optionValue

          return (
            <div className="form-check" key={`${optionValue}-${idx}`}>
              <input
                className="form-check-input"
                type={type}
                value={optionValue}
                checked={isChecked}
                readOnly
                style={{ opacity: 1 }}
              />
              <label className="form-check-label" style={{ color: '#000', fontWeight: '500', opacity: 1 }}>
                {optionValue}
              </label>
            </div>
          )
        })}
      </div>
    )
  }

  const getAnswerForQuestion = (submission, questionId) => {
    if (!submission?.answers || !Array.isArray(submission.answers)) {
      return null
    }
    return submission.answers.find(ans => ans.question_id === questionId) || null
  }

  const renderQuestionWithAnswer = (question, answer) => {
    const getInputValue = () => {
      if (!answer) return ''

      switch (question.question_type) {
        case 'text':
        case 'email':
        case 'number':
        case 'textarea':
          return answer.answer_text || ''
        default:
          return ''
      }
    }

    return (
      <div key={question.question_id} className="mb-3">
        <label className="form-label" style={{ color: "#b26ad1", fontWeight: 600 }}>
          {question.question_text}
          {question.is_required && <span className="text-danger ms-1">*</span>}
        </label>

        {(question.question_type === 'text' || question.question_type === 'email' || question.question_type === 'number') && (
          <input
            type={question.question_type}
            className="form-control"
            value={getInputValue()}
            placeholder="No answer provided"
            readOnly
            style={{ backgroundColor: '#f8f9fa', color: '#000', fontWeight: '500', opacity: 1 }}
          />
        )}

        {question.question_type === 'textarea' && (
          <textarea
            className="form-control"
            rows="3"
            value={getInputValue()}
            placeholder="No answer provided"
            readOnly
            style={{ backgroundColor: '#f8f9fa', color: '#000', fontWeight: '500', opacity: 1 }}
          />
        )}

        {question.question_type === 'radio' && question.options && question.options.length > 0 && (
          renderOptions(question.options, answer?.answer_option, "radio")
        )}

        {question.question_type === 'checkbox' && question.options && question.options.length > 0 && (
          renderOptions(question.options, answer?.answer_options, "checkbox")
        )}

        {(!question.options || question.options.length === 0) &&
          (question.question_type === 'radio' || question.question_type === 'checkbox') && (
            <div className="text-muted">No options available</div>
          )}
      </div>
    )
  }

  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedSubmission(null)
  }

  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission)

    // Initialize edit answers with current submission answers
    const initialAnswers = {}
    if (submission.answers && Array.isArray(submission.answers)) {
      submission.answers.forEach(answer => {
        // Find the question to determine its type
        const question = formData?.groups?.flatMap(group => group.questions || [])
          ?.find(q => q.question_id === answer.question_id)

        if (question && question.question_type === 'checkbox' && answer.answer_options) {
          // For checkbox questions, store array of option IDs, filtering out null values
          initialAnswers[answer.question_id] = answer.answer_options
            .map(opt => opt.id || opt.option_id)
            .filter(id => id !== null && id !== undefined)
        } else if (question && question.question_type === 'radio' && answer.answer_option) {
          // For radio questions, store single option ID
          initialAnswers[answer.question_id] = answer.answer_option.id || answer.answer_option.option_id
        } else {
          // For text-based questions, store the text value
          initialAnswers[answer.question_id] = answer.answer_text || ''
        }
      })
    }
    setEditAnswers(initialAnswers)
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingSubmission(null)
    setEditAnswers({})
  }

  const handleEditAnswerChange = (questionId, value, questionType = 'text') => {
    // Handle date values - convert Date object to YYYY-MM-DD string format
    if (questionType === 'date' && value instanceof Date) {
      const formattedDate = value.toISOString().split('T')[0]
      setEditAnswers(prev => ({
        ...prev,
        [questionId]: formattedDate
      }))
    } else {
      setEditAnswers(prev => ({
        ...prev,
        [questionId]: value
      }))
    }
  }

  const handleEditCheckboxChange = (questionId, optionId, checked) => {
    setEditAnswers(prev => {
      const currentAnswers = prev[questionId] || []
      if (checked) {
        // Only add if optionId is not null and not already in the array
        const validOptionId = optionId !== null && optionId !== undefined ? optionId : null
        if (validOptionId !== null && !currentAnswers.includes(validOptionId)) {
          return {
            ...prev,
            [questionId]: [...currentAnswers, validOptionId]
          }
        }
        return prev
      } else {
        return {
          ...prev,
          [questionId]: currentAnswers.filter(val => val !== optionId && val !== null && val !== undefined)
        }
      }
    })
  }

  const handleSaveEditSubmission = async () => {
    try {
      setLoading(true)

      // Convert editAnswers to the required format
      const answersArray = Object.entries(editAnswers).map(([questionId, answer]) => {
        // Find the question to determine its type
        const question = formData?.groups?.flatMap(group => group.questions || [])
          ?.find(q => q.question_id === parseInt(questionId))

        if (Array.isArray(answer)) {
          return {
            question_id: parseInt(questionId),
            answer_option_ids: answer.filter(id => id !== null && id !== undefined) // Filter out null values
          }
        } else if (question && question.question_type === 'radio') {
          // For radio button answers (single option ID)
          return {
            question_id: parseInt(questionId),
            answer_option_id: parseInt(answer) // Ensure it's an integer option ID for radio
          }
        } else {
          // For text, number, email, date, textarea questions
          return {
            question_id: parseInt(questionId),
            answer_text: String(answer)
          }
        }
      })

      const payload = {
        submission_id: editingSubmission.submission_id,
        answers: answersArray
      }

      const response = await editNormalQuestionaireAnswers(payload)

      if (response.data) {
        toast.success('Submission updated successfully!')
        handleCloseEditModal()
        // Refresh the form data to update the submissions list
        await fetchFormQuestions()
      } else {
        toast.error('Failed to update submission')
      }
    } catch (error) {
      console.error('Error updating submission:', error)
      toast.error(Utils.getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSubmission = async (submission) => {
    try {
      setLoading(true)
      const response = await deleteNormalQuestionaireAnswers({
        submission_id: submission.submission_id
      })

      if (response.data) {
        toast.success('Submission deleted successfully!')
        // Refresh the form data to update the submissions list
        await fetchFormQuestions()
      } else {
        toast.error('Failed to delete submission')
      }
    } catch (error) {
      console.error('Error deleting submission:', error)
      toast.error(Utils.getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }


  // Get unique users (remove duplicates)
  const getUniqueUsers = () => {
    if (!formData?.submissions || !Array.isArray(formData.submissions)) return []

    const uniqueUsers = []
    const seenUserIds = new Set()

    formData.submissions.forEach(submission => {
      if (submission?.user_id && submission?.created_by && !seenUserIds.has(submission.user_id)) {
        seenUserIds.add(submission.user_id)
        uniqueUsers.push({
          user_id: submission.user_id,
          full_name: submission.created_by.full_name || 'Unknown User',
          email: submission.created_by.email || 'No email'
        })
      }
    })

    return uniqueUsers
  }

  // Get submissions for selected user
  const getFilteredSubmissions = () => {
    if (!formData?.submissions || !Array.isArray(formData.submissions)) return []

    if (!selectedUserId) return formData.submissions

    return formData.submissions.filter(submission =>
      submission?.user_id?.toString() === selectedUserId
    )
  }

  const resetUserFilter = () => {
    setSelectedUserId('')
  }

  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}

      <div className="section-bg-color" style={{ minHeight: "calc(100vh - 93px)", paddingTop: "60px" }}>
        <div className="container py-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h1 className="text-white mb-0">Manage Normal Questionnaire</h1>
            <div className="d-flex align-items-center gap-3">
              {!loading && formData?.submissions?.length > 0 && getUniqueUsers().length > 1 && (
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select"
                    style={{ minWidth: '200px' }}
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    <option value="">All Users</option>
                    {getUniqueUsers().map((user) => (
                      <option key={user.user_id} value={user.user_id.toString()}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                  {selectedUserId && (
                    <button
                      className="btn btn-outline-light btn-sm"
                      onClick={resetUserFilter}
                      title="Clear filter"
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              )}
              <button
                className="btn btn-light"
                onClick={handleBack}
                style={{ display: 'inline-flex', alignItems: 'center' }}
              >
                <i className="bi bi-arrow-left me-2"></i> Back
              </button>
            </div>
          </div>

          {!loading && formData && (
            <>
              {/* Submissions */}
              {formData.submissions && formData.submissions.length > 0 ? (
                <div className="mb-4">
                  {/* Submissions Table */}
                  <div className="card mt-4">
                    <div className="card-header bg-info text-white">
                      <h6 className="mb-0">
                        <i className="bi bi-list-ul me-2"></i>
                        {selectedUserId
                          ? `Submissions by ${getUniqueUsers().find(u => u.user_id.toString() === selectedUserId)?.full_name || 'Selected User'}`
                          : 'All Submissions'
                        }
                        ({getFilteredSubmissions().length})
                      </h6>
                    </div>
                    <div className="card-body">
                      {getFilteredSubmissions().length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead>
                              <tr>
                                <th style={{ width: '5%' }}>#</th>
                                <th style={{ width: '25%' }}>Submitted By</th>
                                <th style={{ width: '30%' }}>Email</th>
                                <th style={{ width: '20%' }}>Date</th>
                                <th style={{ width: '20%' }} className="text-center">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getFilteredSubmissions().map((submission, index) => (
                                <tr key={submission.submission_id}>
                                  <td>{index + 1}</td>
                                  <td>{submission.created_by?.full_name || 'Unknown User'}</td>
                                  <td>{submission.created_by?.email || 'No email'}</td>
                                  <td>{new Date(submission.created_at).toLocaleDateString()}</td>
                                  <td className="text-center">
                                    <div className="d-flex justify-content-center gap-2">
                                      <button
                                        className="btn btn-sm btn-primary rounded-circle"
                                        onClick={() => handleViewSubmission(submission)}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title="View Submission"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                      >
                                        <i className="bi bi-eye"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-warning rounded-circle"
                                        onClick={() => handleEditSubmission(submission)}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title="Edit Submission"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger rounded-circle"
                                        onClick={() => handleDeleteSubmission(submission)}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title="Delete Submission"
                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="alert alert-info mb-0">
                          <i className="bi bi-info-circle me-2"></i>
                          No submissions found for the selected user.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body">
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      No answers submitted for this form.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !formData && (
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              No form data available
            </div>
          )}
        </div>
      </div>

      {/* Modal for Submission Details */}
      {showModal && selectedSubmission && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {/* <i className="bi bi-person-circle me-2"></i> */}
                  Submission Details - {selectedSubmission.created_by?.full_name || 'Unknown User'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                >
                  <i className="bi bi-x-circle" aria-hidden="true" />
                </button>
              </div>

              <div className="modal-body">

                {/* Questions and Answers */}
                {formData.groups && formData.groups.length > 0 ? (
                  formData.groups.map((group) => (
                    <div key={group.group_id} className="mb-4">
                      <h6 className="text-primary mb-3 border-bottom pb-2">
                        <i className="bi bi-collection me-2"></i>
                        {group.group_name}
                      </h6>

                      {group.questions && group.questions.length > 0 ? (
                        group.questions.map((question) => {
                          const answer = getAnswerForQuestion(selectedSubmission, question.question_id)
                          return renderQuestionWithAnswer(question, answer)
                        })
                      ) : (
                        <div className="text-muted">No questions in this group.</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-muted">No groups found in this form.</div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal for Submission */}
      {showEditModal && editingSubmission && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {/* <i className="bi bi-pencil-square me-2"></i> */}
                  Edit Submission - {editingSubmission.created_by?.full_name || 'Unknown User'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                  aria-label="Close"
                >
                  <i className="bi bi-x-circle" aria-hidden="true" />
                </button>
              </div>

              <div className="modal-body">
                {/* Questions and Answers for Editing */}
                {formData.groups && formData.groups.length > 0 ? (
                  formData.groups.map((group) => (
                    <div key={group.group_id} className="mb-4">
                      <h6 className="text-primary mb-3 border-bottom pb-2">
                        <i className="bi bi-collection me-2"></i>
                        {group.group_name}
                      </h6>

                      {group.questions && group.questions.length > 0 ? (
                        group.questions.map((question) => (
                          <div key={question.question_id} className="mb-3 p-3 border rounded">
                            <label className="form-label fw-bold">
                              {question.question_text}
                              {question.is_required && (
                                <span className="text-danger ms-1">*</span>
                              )}
                            </label>

                            {/* Text Input */}
                            {question.question_type === 'text' && (
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter your answer..."
                                value={editAnswers[question.question_id] || ''}
                                onChange={(e) => handleEditAnswerChange(question.question_id, e.target.value)}
                              />
                            )}

                            {/* Number Input */}
                            {question.question_type === 'number' && (
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Enter number..."
                                value={editAnswers[question.question_id] || ''}
                                onChange={(e) => handleEditAnswerChange(question.question_id, e.target.value)}
                              />
                            )}

                            {/* Email Input */}
                            {question.question_type === 'email' && (
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Enter email address..."
                                value={editAnswers[question.question_id] || ''}
                                onChange={(e) => handleEditAnswerChange(question.question_id, e.target.value)}
                              />
                            )}

                            {/* Date Input */}
                            {question.question_type === 'date' && (
                              <div className="position-relative">
                                <DatePicker
                                  ref={(el) => (datePickerRefs.current[question.question_id] = el)}
                                  selected={editAnswers[question.question_id] ? new Date(editAnswers[question.question_id]) : null}
                                  onChange={(date) => handleEditAnswerChange(question.question_id, date, 'date')}
                                  className="form-control"
                                  placeholderText="dd-mm-yyyy"
                                  dateFormat="dd/MM/yyyy"
                                  showPopperArrow={false}
                                  isClearable={false}
                                  dropdownMode="select"
                                  shouldCloseOnSelect={true}
                                  autoComplete="off"
                                  openToDate={new Date()}
                                />
                                <i
                                  className="bi bi-calendar"
                                  style={{
                                    position: "absolute",
                                    right: "16px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    color: "#888",
                                    fontSize: "16px"
                                  }}
                                  onClick={() => datePickerRefs.current[question.question_id]?.setFocus(true)}
                                />
                              </div>
                            )}

                            {/* Textarea */}
                            {question.question_type === 'textarea' && (
                              <textarea
                                className="form-control"
                                rows="4"
                                placeholder="Enter your answer..."
                                value={editAnswers[question.question_id] || ''}
                                onChange={(e) => handleEditAnswerChange(question.question_id, e.target.value)}
                              ></textarea>
                            )}

                            {/* Radio Options */}
                            {question.question_type === 'radio' && question.options && question.options.length > 0 && (
                              <div>
                                {question.options.map((option, optionIndex) => (
                                  <div key={optionIndex} className="form-check mb-2">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={`edit_question_${question.question_id}`}
                                      id={`edit_question_${question.question_id}_option_${optionIndex}`}
                                      value={option.id || option.option_id}
                                      checked={editAnswers[question.question_id] === (option.id || option.option_id)}
                                      onChange={(e) => handleEditAnswerChange(question.question_id, parseInt(e.target.value))}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`edit_question_${question.question_id}_option_${optionIndex}`}
                                    >
                                      {option.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Checkbox Options */}
                            {question.question_type === 'checkbox' && question.options && question.options.length > 0 && (
                              <div>
                                {question.options.map((option, optionIndex) => (
                                  <div key={optionIndex} className="form-check mb-2">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit_question_${question.question_id}_option_${optionIndex}`}
                                      value={option.id || option.option_id}
                                      checked={(editAnswers[question.question_id] || []).includes(option.id || option.option_id)}
                                      onChange={(e) => {
                                        const optionId = parseInt(e.target.value)
                                        if (!isNaN(optionId)) {
                                          handleEditCheckboxChange(question.question_id, optionId, e.target.checked)
                                        }
                                      }}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`edit_question_${question.question_id}_option_${optionIndex}`}
                                    >
                                      {option.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-muted">No questions in this group.</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-muted">No groups found in this form.</div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseEditModal}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveEditSubmission}
                  disabled={loading}
                >
                  <i className="bi bi-check-lg me-1"></i>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ManageNormalQuestionnareDetails
